import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Strikethrough,
  Highlighter,
  List as ListIcon,
  ListOrdered,
  Eraser,
} from 'lucide-react';
import { createBoardNoteExtensions } from '../../../utils/richCommentExtensions';
import { handleCommentListKeyDown } from '../../../utils/richCommentListEditor';
import {
  htmlToPlainWithMentions,
  isHtmlEmpty,
  sanitizeCommentHtml,
} from '../../../utils/richComment';
import type { CommentMention, CRMUser } from '../../../types';
import '../../../components/pm/TaskComments.css';

interface BoardNoteEditorProps {
  initialHtml?: string;
  initialPlain?: string;
  fontSize?: number;
  mentionableUsers?: CRMUser[];
  onChange: (payload: { textHtml: string; text: string; mentions: CommentMention[] }) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

function insertOrToggleList(editor: Editor, kind: 'bullet' | 'ordered') {
  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;
  const command = kind === 'bullet' ? 'toggleBulletList' : 'toggleOrderedList';

  if (hasSelection || editor.isActive('bulletList') || editor.isActive('orderedList')) {
    editor.chain().focus()[command]().run();
    return;
  }

  const $pos = editor.state.doc.resolve(from);
  const currentBlock = $pos.parent;
  if (currentBlock.content.size === 0) {
    editor.chain().focus()[command]().run();
    return;
  }

  const listType = kind === 'bullet' ? 'bulletList' : 'orderedList';
  const insertAt = $pos.after();
  editor
    .chain()
    .focus()
    .insertContentAt(insertAt, {
      type: listType,
      content: [{ type: 'listItem', content: [{ type: 'paragraph' }] }],
    })
    .focus(insertAt + 3)
    .run();
}

const ToolbarBtn: React.FC<{
  editor: Editor | null;
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ editor, active, onClick, title, children }) => (
  <button
    type="button"
    className={`rce-tb-btn${active ? ' rce-tb-btn--active' : ''}`}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={!editor}
  >
    {children}
  </button>
);

const BoardNoteToolbar: React.FC<{ editor: Editor | null }> = ({ editor }) => (
  <div className="rce-toolbar board-note-rte-toolbar nodrag nowheel nopan">
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('bold')}
      onClick={() => editor?.chain().focus().toggleBold().run()}
      title="Bold"
    >
      <BoldIcon size={14} />
    </ToolbarBtn>
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('italic')}
      onClick={() => editor?.chain().focus().toggleItalic().run()}
      title="Italic"
    >
      <ItalicIcon size={14} />
    </ToolbarBtn>
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('strike')}
      onClick={() => editor?.chain().focus().toggleStrike().run()}
      title="Strikethrough"
    >
      <Strikethrough size={14} />
    </ToolbarBtn>
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('highlight')}
      onClick={() => editor?.chain().focus().toggleHighlight().run()}
      title="Highlight"
    >
      <Highlighter size={14} />
    </ToolbarBtn>
    <span className="rce-tb-divider" />
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('bulletList')}
      onClick={() => editor && insertOrToggleList(editor, 'bullet')}
      title="Bullet list"
    >
      <ListIcon size={14} />
    </ToolbarBtn>
    <ToolbarBtn
      editor={editor}
      active={editor?.isActive('orderedList')}
      onClick={() => editor && insertOrToggleList(editor, 'ordered')}
      title="Numbered list"
    >
      <ListOrdered size={14} />
    </ToolbarBtn>
    <span className="rce-tb-divider" />
    <ToolbarBtn
      editor={editor}
      onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
      title="Clear formatting"
    >
      <Eraser size={14} />
    </ToolbarBtn>
  </div>
);

const BoardNoteEditor: React.FC<BoardNoteEditorProps> = ({
  initialHtml,
  initialPlain,
  fontSize = 14,
  mentionableUsers,
  onChange,
  placeholder = 'Double-click to edit',
  autoFocus = false,
}) => {
  const initialContent = useMemo(() => {
    if (initialHtml) return initialHtml;
    if (initialPlain) return `<p>${initialPlain.replace(/\n/g, '<br>')}</p>`;
    return '';
  }, [initialHtml, initialPlain]);

  const mentionableRef = useRef(mentionableUsers || []);
  mentionableRef.current = mentionableUsers || [];

  const emitChange = useCallback(
    (html: string) => {
      const sanitized = sanitizeCommentHtml(html);
      const { plain, mentions } = htmlToPlainWithMentions(sanitized);
      onChange({
        textHtml: isHtmlEmpty(sanitized) ? '' : sanitized,
        text: plain,
        mentions,
      });
    },
    [onChange],
  );

  const editor: Editor | null = useEditor({
    extensions: createBoardNoteExtensions(placeholder, () => mentionableRef.current),
    content: initialContent,
    editable: true,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        // nodrag/nowheel/nopan: text entry must not swallow React Flow node moves (CRM #31).
        class: 'rce-content nodrag nowheel nopan',
        style: `font-size: ${fontSize}px`,
      },
      handleKeyDown(_view, event) {
        if (!editor) return false;
        return handleCommentListKeyDown(editor, event);
      },
    },
    onUpdate: ({ editor: ed }) => emitChange(ed.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          class: 'rce-content nodrag nowheel nopan',
          style: `font-size: ${fontSize}px`,
        },
        handleKeyDown(_view, event) {
          return handleCommentListKeyDown(editor, event);
        },
      },
    });
  }, [editor, fontSize]);

  // React Flow selection/focus can win the race against TipTap's autofocus option.
  useEffect(() => {
    if (!autoFocus || !editor) return;
    const t = window.setTimeout(() => {
      if (!editor.isDestroyed && !editor.isFocused) editor.commands.focus('end');
    }, 0);
    return () => window.clearTimeout(t);
  }, [autoFocus, editor]);

  return (
    <div className="rce-root board-note-rte">
      <BoardNoteToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default BoardNoteEditor;
